import React, { Component, Fragment } from 'react';
import API from "../../../../../api/api";
// components
import { Input, Row, Col, TabPane } from 'reactstrap';
import { Callout } from 'components/ui';
import { NavigationRow } from '../rows/registration/NavigationRow';
import { Form, Radio } from "components/inputs";
// functions
import { toast } from 'react-toastify';

// the teacher email becomes the username of their account, so we only accept one address
const EMAIL_PATTERN = /^[^\s,;@]+@[^\s,;@]+\.[^\s,;@]+$/;
const NOT_UPDATED_MESSAGE = 'Please update this teacher for the coming year.';

const normalize = email => ( email || '' ).toString().trim().toLowerCase();
// nothing to sort out, the email is good as it is
const OK = { type: '', message: '' };
const error = message => ({ type: 'error', message });

export class PlatoonsTab extends Component {

  state = { platoons: [], checks: {}, checking: {} }

  // the inputs we push validation messages onto
  emailInputs = {};
  updatedInputs = {};
  // answers of the account lookup, so we do not ask the server for the same email twice
  lookupCache = {};

  componentDidMount() {
    API.get( `/core/platoons` )
      .then(platoons => {
        // the updated flag is still on the platoon from the registration of last year, so
        // the base commander would never see it again. Every registration confirms its own
        // teachers, so the tab starts with nothing ticked off.
        this.setState({ platoons: platoons.map( platoon => ({ ...platoon, updated: null }) ) })
      })
  }

  componentDidUpdate() {
    this.applyValidity()
  }

  // keep the browser validation of the form in sync with our own checks
  applyValidity = () => {
    this.state.platoons.forEach( platoon => {
      const email = this.emailInputs[ platoon.class_id ];
      if ( email ) email.setCustomValidity( this.emailMessage( platoon.class_id ) );

      const updated = this.updatedInputs[ platoon.class_id ];
      if ( updated ) updated.setCustomValidity( this.isUpdated( platoon ) ? '' : NOT_UPDATED_MESSAGE );
    });
  }

  // what is still wrong with the email of a platoon, an empty string when it is settled
  emailMessage = class_id => {
    const check = this.state.checks[ class_id ];
    return check && check.type === 'error' ? check.message : '';
  }

  isUpdated = platoon => parseInt( platoon.updated, 10 ) === 1;

  setValue = (id, key, value) => {
    const platoons = this.state.platoons.map(platoon => {
      if (platoon.class_id !== id) return platoon
      else {
        const pl = {}
        if (key.includes('updated')) pl['updated'] = value
        else pl[key] = value
        return {...platoon, ...pl}
      }
    })
    // a new email address has not been looked up yet, drop what we knew about the old one
    const checks = { ...this.state.checks };
    if ( key === 'email' ) {
      delete checks[ id ];
      if ( this.emailInputs[ id ] ) this.emailInputs[ id ].setCustomValidity('');
    }
    this.setState({ platoons, checks })
  }

  /**
   * ask the server what the address means for this platoon: an account we may use, or
   * one that belongs to another base and has to be corrected.
   */
  lookupAccount = ( class_id, email ) => {
    const key = `${ class_id }:${ email }`;
    if ( this.lookupCache[ key ] ) return Promise.resolve( this.lookupCache[ key ] );

    return API.get( `/core/platoons?action=teacher&id=${ class_id }&email=${ encodeURIComponent( email ) }` )
      .then( account => {
        this.lookupCache[ key ] = account;
        return account;
      })
      // the same lookup runs on the server before the card is charged, so a network
      // problem here should not stop the base from registering
      .catch( () => ({ status: 'available' }) );
  }

  // works out what is left to do for the email of a platoon
  checkEmail = ( platoon, platoons = this.state.platoons ) => {
    const email = normalize( platoon.email );

    if ( !email )
      return Promise.resolve( error('A teacher email address is required.') );

    if ( !EMAIL_PATTERN.test( email ) )
      return Promise.resolve( error('Please enter one valid email address for this teacher.') );

    // a platoon above this one already uses it, the same teacher takes both platoons and
    // the answer of the server would be the same, so there is nothing to ask
    const index = platoons.findIndex( other => other.class_id === platoon.class_id );
    const twin = platoons.some(
      ( other, position ) => position < index && normalize( other.email ) === email
    );
    if ( twin ) return Promise.resolve( OK );

    return this.lookupAccount( platoon.class_id, email ).then( account => {
      // an account that has nothing to do with this base, most likely a typo
      if ( account.status === 'outside' )
        return error(
          `${ email } belongs to an account that is not part of your base. `
          + 'Please use a different address, or ask the office to connect that account.'
        );

      // an account we may use is nothing to bother the base commander with, registration
      // connects it to the platoon on its own
      return OK;
    });
  }

  // check a single teacher email, used when the base commander leaves the field
  validateOne = class_id => {
    const platoon = this.state.platoons.find( p => p.class_id === class_id );
    if ( !platoon ) return Promise.resolve( OK );

    this.setState({ checking: { ...this.state.checking, [ class_id ]: true } });

    return this.checkEmail( platoon ).then( check => {
      this.setState({
        checking: { ...this.state.checking, [ class_id ]: false },
        checks: { ...this.state.checks, [ class_id ]: check },
      }, this.applyValidity );
      return check;
    });
  }

  save = elem => {
    elem.preventDefault()
    elem.persist()
    const form = elem.target;
    const { platoons } = this.state;

    // the account lookups take a moment, so the button says what it is waiting for
    this.setState({ saving: true, stage: 'checking' });

    return Promise.all( platoons.map( platoon => this.checkEmail( platoon, platoons ) ) )
      .then( results => {
        const checks = {};
        platoons.forEach( ( platoon, index ) => { checks[ platoon.class_id ] = results[ index ] });

        return new Promise( resolve => {
          this.setState({ checks }, () => { this.applyValidity(); resolve() });
        });
      })
      .then( () => {
        const notUpdated = platoons.filter( platoon => !this.isUpdated( platoon ) );
        const unsettled = platoons.filter( platoon => !!this.emailMessage( platoon.class_id ) );
        // every teacher has to be updated and their account has to be settled before we
        // can move on. The accounts are created once the registration is paid for.
        if ( notUpdated.length || unsettled.length ) {
          this.setState({ saving: false });
          this.props.onValidChange( false );
          if ( form.reportValidity ) form.reportValidity();
          return toast.error( notUpdated.length
            ? 'Every teacher has to be updated for the coming year before you can continue.'
            : 'Please check the teacher email addresses that are marked below.'
          );
        }
        // the teacher logins are built off of this information, so save it before moving on
        this.setState({ stage: 'saving' });
        return Promise.all(
          platoons.map( platoon => API.patch('/core/platoons?id=' + platoon.class_id, platoon) )
        )
        .then( () => {
          this.setState({ saving: false });
          this.props.onSubmit( elem );
        })
        .catch( err => {
          this.setState({ saving: false });
          toast.error( err.message );
        });
      });
  }

  renderEmailCheck = platoon => {
    if ( this.state.checking[ platoon.class_id ] )
      return <div className='text-muted'><small>Checking this email address&hellip;</small></div>;

    const message = this.emailMessage( platoon.class_id );
    if ( !message ) return null;

    return (
      <div className='invalid-message' style={{ maxHeight: '75px' }}>
        { message }
      </div>
    );
  }

  render() {
    const { tabId, back, onValidChange } = this.props
    const { saving, stage } = this.state
    const gradeTitleCss = {
      borderBottom: '1px solid #3e6dc4',
      fontSize: '1.2em',
      paddingLeft: '0.5em',
      marginTop: '0.5em',
      marginBottom: '0.5rem',
      fontFamily: 'Arial,Helvetica,sans-serif'
    }

    return (
      <TabPane tabId={ tabId }>

        <Form
          validateAfterSubmit
          onSubmit={ this.save }
          onValidChange={ onValidChange }>
          <p className='title'>
            Platoons
          </p>

          <Callout color="warning">
            Please review the platoons that you have and update the teachers information so that they can stay in the loop throughout the year.
            Every teacher needs an email address &mdash; we open an account for them with it so that they can log in to their platoon.
            Teachers who already have an account with us, as a parent or from another platoon, keep the account they have.
          </Callout>

          {this.state.platoons.map(platoon =>
            <Fragment key={platoon.class_id}>
              <p style={ gradeTitleCss }>
                { platoon.class_grade }
                { platoon.class_sub ? '-' + platoon.class_sub : '' }
              </p>
              <Row>
                <Col sm={6}>
                  <label>Teacher Name</label>
                  <Input required name='class_teacher' defaultValue={ platoon.teacher } onChange={ e => this.setValue(platoon.class_id, e.target.name, e.target.value) } />
                </Col>

                <Col sm={6}>
                  <label>Teacher Phone</label>
                  <Input required name='cell' type='tel' defaultValue={ platoon.cell } onChange={ e => this.setValue(platoon.class_id, 'cell', e.target.value) }
                         title='separate multiple email addresses with a comma' // one or more valid phone numbers
                    //pattern='^(((\+[0-9]{1,3}[0-9 ]{9,})|((?:1 |\()?[0-9]{3}(?: |\) |-)?[0-9]{3}(?: |-)?[0-9]{4}))[,;])*((\+[0-9]{1,3}[0-9 ]{9,})|((?:1 |\()?[0-9]{3}(?: |\) |-)?[0-9]{3}(?: |-)?[0-9]{4}))$'
                  />
                  <div className='invalid-message'>separate multiple email addresses with a comma</div>
                </Col>

                <Col sm={6}>
                  <label>Teacher E-Mail</label>
                  <Input
                    required
                    name='email'
                    type='email'
                    value={ platoon.email || '' }
                    title='the teacher logs in with this email address'
                    innerRef={ node => { this.emailInputs[ platoon.class_id ] = node } }
                    onBlur={ () => this.validateOne( platoon.class_id ) }
                    onChange={ e => this.setValue(platoon.class_id, e.target.name, e.target.value) } />

                  { this.renderEmailCheck( platoon ) }
                </Col>

                <Col sm={6}>
                  <label>Updated for this yr</label><br />
                  <Radio
                    required
                    name={ platoon.class_id + '_updated' }
                    value='1'
                    className='form-check-input'
                    innerRef={ node => { this.updatedInputs[ platoon.class_id ] = node } }
                    checked={ parseInt(platoon.updated, 10) === 1 }
                    onChange={ e => this.setValue(platoon.class_id, e.target.name, e.target.value) }> This Teacher IS UPDATED for the coming yr </Radio><br />
                  <Radio
                    required
                    name={ platoon.class_id + '_updated' }
                    value='0'
                    className='form-check-input'
                    checked={ parseInt(platoon.updated, 10) === 0 }
                    onChange={ e => this.setValue(platoon.class_id, e.target.name, e.target.value) }> This Teacher IS NOT YET UPDATED for the coming yr </Radio>

                  { !this.isUpdated( platoon ) &&
                    <div className='invalid-message' style={{ maxHeight: '50px' }}>
                      { NOT_UPDATED_MESSAGE }
                    </div>
                  }
                </Col>
              </Row>
            </Fragment>
          )}

          <NavigationRow back={ back } next
            busy={ !!saving }
            nextDisabled={ !!saving }
            busyLabel={ stage === 'saving'
              ? 'Saving your platoons'
              : 'Checking teacher accounts' } />
        </Form>
      </TabPane>
    )
  }
}




// WEBPACK FOOTER //
// ./src/pages/base-managment/base/includes/tabs/PlatoonsTab.jsx